import { useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { BookOpen, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { searchHelpArticles, visibleHelpArticles } from "@/help/helpContent";
import HelpArticle from "@/components/help/HelpArticle";
import { Input } from "@/components/ui/input";

const COPY = {
  de: {
    title: "Hilfe-Center",
    subtitle: "Alle wichtigen Abläufe einfach und Schritt für Schritt erklärt.",
    search: "Anleitungen durchsuchen…",
    steps: "Schritt für Schritt",
    tips: "Wichtige Hinweise",
    next: "Nächster Schritt",
    empty: "Keine passende Anleitung gefunden.",
  },
  en: {
    title: "Help Center",
    subtitle: "Every important workflow explained clearly, step by step.",
    search: "Search guides…",
    steps: "Step by step",
    tips: "Important tips",
    next: "Next step",
    empty: "No matching guide found.",
  },
};

export default function HelpCenter() {
  const { locale } = useI18n();
  const { can, modules, tenantRole } = useAuth();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const labels = COPY[locale] || COPY.en;

  const available = useMemo(() => visibleHelpArticles({
    locale, pathname: location.pathname, can, modules, tenantRole,
  }), [locale, location.pathname, can, modules, tenantRole]);
  const results = useMemo(() => searchHelpArticles(available, query), [available, query]);
  const requestedId = params.get("article");
  const selected = results.find((item) => item.id === requestedId) || results[0];

  return (
    <div className="space-y-6" data-testid="help-center">
      <header>
        <h1 className="flex items-center gap-3 font-heading text-3xl font-bold tracking-tight">
          <BookOpen className="h-7 w-7 text-primary" />{labels.title}
        </h1>
        <p className="mt-1 text-muted-foreground">{labels.subtitle}</p>
      </header>

      <div className="relative max-w-2xl">
        <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={labels.search}
          aria-label={labels.search}
          className="pl-9"
          data-testid="help-center-search"
        />
      </div>

      {selected ? (
        <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
          <nav className="space-y-2" aria-label={labels.title}>
            {results.map((item) => (
              <button
                key={item.id}
                onClick={() => setParams({ article: item.id })}
                className={`w-full rounded-xl border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  item.id === selected.id ? "border-primary bg-primary/5" : "hover:bg-muted"
                }`}
                data-testid={`help-center-item-${item.id}`}
              >
                <span className="block text-sm font-semibold">{item.title}</span>
                <span className="mt-1 block text-xs leading-5 text-muted-foreground">{item.summary}</span>
              </button>
            ))}
          </nav>
          <div className="rounded-2xl border bg-card p-5 shadow-sm sm:p-8">
            <HelpArticle article={selected} labels={labels} />
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed p-12 text-center text-muted-foreground">{labels.empty}</div>
      )}
    </div>
  );
}
